import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material'
import { SESSION_EXPIRED_EVENT } from '../../services/sessionEvents'

interface SessionExpiredModalProps {
  /** Called when the user clicks "Sign in". Trigger your existing login flow here. */
  onSignIn: () => void
}

/**
 * Listens for the `ignis:session-expired` event and shows a non-intrusive
 * modal asking the user to sign in again. The current path is saved to
 * sessionStorage so the login flow can redirect back after re-auth.
 *
 * Debounced: rapid repeated events do not stack modals — the first one wins.
 */
export const SessionExpiredModal = ({ onSignIn }: SessionExpiredModalProps) => {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const handleExpired = () => {
      // Ignore if already showing — debounce rapid repeated events
      setOpen((current) => {
        if (current) return current
        return true
      })
    }

    window.addEventListener(SESSION_EXPIRED_EVENT, handleExpired)
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleExpired)
  }, [])

  const handleSignIn = () => {
    // Save current location so login can redirect back
    const returnTo = window.location.pathname + window.location.search
    if (returnTo && returnTo !== '/') {
      sessionStorage.setItem('ignis_return_to', returnTo)
    }
    setOpen(false)
    onSignIn()
  }

  const handleDismiss = () => {
    setOpen(false)
  }

  return (
    <Dialog
      open={open}
      onClose={handleDismiss}
      aria-labelledby="session-expired-title"
      aria-describedby="session-expired-description"
      PaperProps={{
        sx: {
          borderRadius: 2,
          minWidth: 360,
        },
      }}
    >
      <DialogTitle id="session-expired-title" sx={{ fontWeight: 600 }}>
        Session expired
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="session-expired-description">
          Your session has expired. Please sign in again to continue.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={handleDismiss} color="inherit" variant="text">
          Dismiss
        </Button>
        <Button onClick={handleSignIn} variant="contained" color="primary" autoFocus>
          Sign in
        </Button>
      </DialogActions>
    </Dialog>
  )
}
